import { FACE_SIMILARITY_THRESHOLD, IDV_OVERALL_STATUS, MAX_RETRIES } from 'constants/idv';
import { parseIdKey } from 'helpers/ids';

import { MessageType } from 'components/FormComponents/FormMessage/FormMessage';

import i18n from '../i18n';

/**
 * Extract field codes from displayFields array recursively.
 * Treats subFields and altIdFields the same (altIdFields are groups, each with subFields).
 */
export const extractFieldCodes = fields => {
  const codes = [];
  if (!Array.isArray(fields)) return codes;
  fields.forEach(field => {
    if (field.code) {
      codes.push(field.code);
    }
    if (field.subFields) {
      codes.push(...extractFieldCodes(field.subFields));
    }
    const altIdGroups = field.altIdFields || field.alt_id_fields;
    if (Array.isArray(altIdGroups)) {
      altIdGroups.forEach(group => {
        codes.push(...extractFieldCodes(group.subFields || group.sub_fields || []));
      });
    }
  });
  return codes;
};

export const filterUploadsForField = (uploads, fieldCode) =>
  fieldCode == null
    ? uploads || []
    : (uploads || []).filter(upload => upload?.metadata?.field_code === fieldCode);

/**
 * Same conditions handleVerificationResponse uses inline to decide whether a failure should
 * offer a selfie retake instead of (or alongside) a generic re-upload message.
 */
export const isFaceMismatchFailure = ({
  faceSimilarity,
  faceSimilarityStatus = null,
  hasPhoto = null,
  attempts = 0,
}) =>
  typeof faceSimilarity === 'number' &&
  faceSimilarity < FACE_SIMILARITY_THRESHOLD &&
  (attempts || 0) < MAX_RETRIES &&
  faceSimilarityStatus !== 'not_applicable' &&
  hasPhoto === true;

export const getUnableToVerifyMessage = () => {
  const idUploadTranslations = i18n.t('idUpload', {
    ns: 'id_document_types',
    returnObjects: true,
  });
  return (
    idUploadTranslations?.reasonForFailure?.unable_to_verify ||
    'We couldn’t verify the document you uploaded. This may happen if the image is blurry, too dark, cropped, or difficult to read. Please upload a clearer photo and try again. If the issue continues, please contact our support team for assistance.'
  );
};

export const getTranslatedFailureMessage = (
  uploadError,
  translations = null,
  faceSimilarity = undefined,
) => {
  const failureKeys = Array.isArray(uploadError)
    ? uploadError.filter(Boolean)
    : typeof uploadError === 'string' && uploadError
      ? [uploadError]
      : [];

  const hasNumericFaceScore = typeof faceSimilarity === 'number';
  const filteredKeys = failureKeys.filter(key => key !== 'face_mismatch' || hasNumericFaceScore);

  if (filteredKeys.length === 0) return null;

  const idUploadTranslations =
    translations ||
    i18n.t('idUpload', {
      ns: 'id_document_types',
      returnObjects: true,
    });

  const messages = filteredKeys
    .map(key => idUploadTranslations?.reasonForFailure?.[key])
    .filter(Boolean);

  return messages.length > 0 ? messages.join(' ') : null;
};

export const ACCEPTED_AFTER_RETRIES_MESSAGE =
  "We've accepted your document. Our support team will manually verify and contact you if needed.";

export const shouldAcceptAfterRetries = (attempts, status) =>
  (attempts || 0) >= MAX_RETRIES &&
  !!status &&
  (status === IDV_OVERALL_STATUS.FAILED ||
    status === IDV_OVERALL_STATUS.REJECTED ||
    status === IDV_OVERALL_STATUS.NEEDS_REVIEW);

/**
 * Validate retry count and handle max retries scenario
 * @param {Object} upload - Upload object with metadata
 * @param {Object} callbacks - Object with callback functions for state updates
 * @param {Function} callbacks.setError - Function to set error message
 * @param {Function} callbacks.setVerificationError - Function to set verification error
 * @param {Function} callbacks.setErrorType - Function to set error type
 * @param {Function} callbacks.setStatus - Function to set status
 * @param {Function} callbacks.onChange - Function to call on change
 * @param {Function} callbacks.setIsFileRejected - Function to set rejected state
 * @param {boolean} useModel - Whether using model or ID
 * @param {string} status - Current verification status (optional, should be passed from handleVerificationResponse)
 */
export const validateRetryCount = (upload, callbacks, useModel = false, status = null) => {
  const currentAttempts = upload?.metadata?.attempts || 0;

  if (shouldAcceptAfterRetries(currentAttempts, status)) {
    callbacks.setError(ACCEPTED_AFTER_RETRIES_MESSAGE);
    if (callbacks.setVerificationError) {
      callbacks.setVerificationError(null);
    }
    callbacks.setErrorType(MessageType.Warning);
    callbacks.setStatus(IDV_OVERALL_STATUS.NEEDS_REVIEW);
    callbacks.onChange(useModel ? upload : upload?.id || null);
    if (callbacks.setIsFileRejected) {
      callbacks.setIsFileRejected(false);
    }
  }
};

export const handleVerificationResponse = async (data, options, callbacks) => {
  const { getUpload, useModel, value, uploadId } = options;

  const {
    setStatus,
    setError,
    setErrorType,
    setVerificationError,
    setShowSelfieModal,
    onChange,
    setIsFileRejected,
    validateRetryCount: validateRetry,
  } = callbacks;

  // Get upload to check actual attempt count (source of truth)
  const upload = useModel ? value : await getUpload(uploadId);
  // Use metadata.attempts as source of truth (1-indexed: 1, 2, 3)
  const currentAttempts = upload?.metadata?.attempts ?? 0;

  setStatus(data?.status);

  // Handle skip_verification documents (status = not_applicable)
  // These are reference-only documents that don't need verification
  if (data?.status === IDV_OVERALL_STATUS.NOT_APPLICABLE) {
    if (setIsFileRejected) {
      setIsFileRejected(false);
    }
    if (onChange) {
      onChange(useModel ? value : uploadId);
    }
    return { success: true };
  }

  const getFaceMismatchMessage = () => {
    const idUploadTranslations = i18n.t('idUpload', {
      ns: 'id_document_types',
      returnObjects: true,
    });
    return (
      idUploadTranslations?.reasonForFailure?.face_mismatch ||
      'The face in the document does not match the face in the selfie.'
    );
  };

  const hasNumericFaceScore = typeof data?.face_similarity === 'number';
  const isFaceMismatch =
    hasNumericFaceScore &&
    data.face_similarity < FACE_SIMILARITY_THRESHOLD &&
    currentAttempts < MAX_RETRIES &&
    data?.face_similarity_status !== 'not_applicable' &&
    data?.has_photo === true;
  const failureReasons = Array.isArray(data?.reason_for_failure) ? data.reason_for_failure : [];
  const hasOtherFailures = failureReasons.some(reason => reason && reason !== 'face_mismatch');

  // Handle face similarity issues (only when face is the sole failure reason)
  if (isFaceMismatch && callbacks.reTakeSelfie) {
    if (setShowSelfieModal) {
      setShowSelfieModal(true);
    }
    setStatus(data?.status || IDV_OVERALL_STATUS.FAILED);
    if (setVerificationError) {
      setVerificationError(getFaceMismatchMessage());
    }
    if (validateRetry) {
      validateRetry(upload, callbacks, useModel, data?.status);
    }
    return { success: false, needsRetry: true };
  } else if (isFaceMismatch && !hasOtherFailures) {
    setStatus(data?.status || IDV_OVERALL_STATUS.FAILED);
    if (setVerificationError) {
      setVerificationError(getFaceMismatchMessage());
    }
    if (validateRetry) {
      validateRetry(upload, callbacks, useModel, data?.status);
    }
    return { success: false, needsRetry: true };
  }

  // Handle failed verification
  const hasFailure =
    data?.status === 'failed' ||
    (Array.isArray(data?.reason_for_failure) && data.reason_for_failure.length > 0);
  if (hasFailure) {
    // Check if we're at max retries - if so, validateRetryCount will handle the messaging
    if (currentAttempts >= MAX_RETRIES) {
      if (validateRetry) {
        validateRetry(upload, callbacks, useModel, data?.status);
      }
      return { success: false };
    }

    // Not at max retries yet - show error message
    if (setVerificationError) {
      const translatedMessage = getTranslatedFailureMessage(
        data?.reason_for_failure,
        null,
        data?.face_similarity,
      );
      setVerificationError(translatedMessage || getUnableToVerifyMessage());
    }
    if (validateRetry) {
      validateRetry(upload, callbacks, useModel, data?.status);
    }
    return { success: false };
  }

  // Verification successful
  if (setIsFileRejected) {
    setIsFileRejected(false);
  }
  // Call onChange on success
  if (onChange) {
    onChange(useModel ? value : uploadId);
  }
  return { success: true };
};

export const verifyIdDocument = async (options, callbacks) => {
  const {
    uploadId,
    metadata,
    listCode,
    expiryTolerance,
    verifyByToken,
    reTakeSelfie,
    getUpload,
    useModel,
    value,
    skipVerification = false,
  } = options;

  const {
    setVerifying,
    setStatus,
    setError,
    setErrorType,
    setVerificationError,
    setShowSelfieModal,
    onChange,
    setIsFileRejected,
  } = callbacks;

  const idCode = metadata?.id_type || null;
  if (!idCode) {
    return { success: false, error: 'No ID code provided' };
  }

  const { idType, countryCode } = parseIdKey(idCode);

  if (setVerifying) {
    setVerifying(true);
  }

  try {
    const data = await verifyByToken({
      listCode,
      uploadId,
      idCode,
      idType,
      idCountry: countryCode || null,
      expiryTolerance,
      skipVerification,
    });

    const result = await handleVerificationResponse(
      data,
      {
        verifyByToken,
        getUpload,
        metadata,
        listCode,
        expiryTolerance,
        reTakeSelfie,
        useModel,
        value,
        uploadId,
      },
      {
        ...callbacks,
        reTakeSelfie,
        validateRetryCount:
          callbacks.validateRetryCount ||
          ((upload, cb, useModel, status) => validateRetryCount(upload, cb, useModel, status)),
      },
    );

    return result;
  } catch (error) {
    console.error('Error verifying ID document:', error);
    if (setVerificationError) {
      setVerificationError(error?.message || 'Verification failed');
    }

    // Get upload for retry validation
    const upload = useModel ? value : await getUpload(uploadId);
    // In catch block, we don't have a status from the response, so pass null
    // validateRetryCount won't show the "accepted" message if status is null
    validateRetryCount(upload, callbacks, useModel, null);

    return { success: false, error: error?.message || 'Verification failed' };
  } finally {
    if (setVerifying) {
      setVerifying(false);
    }
  }
};

/**
 * Calculate border status based on upload status
 * @param {string} status - Upload status
 * @returns {string} Border status class
 */
export const getBorderStatus = status => {
  if (status === IDV_OVERALL_STATUS.FAILED || status === IDV_OVERALL_STATUS.REJECTED) {
    return 'u-border-error';
  }
  // verified, needs_review, and not_applicable all show success border
  if (
    status === IDV_OVERALL_STATUS.VERIFIED ||
    status === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
    status === IDV_OVERALL_STATUS.NOT_APPLICABLE
  ) {
    return 'u-border-success';
  }
  return '';
};
